import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule, Router } from '@angular/router';
import { SidebarComponent } from '../../components/sidebar/sidebar.component';
import { NavbarComponent } from '../../components/navbar/navbar.component';
import { OrderService } from '../../core/services/order.service';
import { Order, OrderStatus } from '../../models/order.model';

@Component({
  selector: 'app-order-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, SidebarComponent, NavbarComponent],
  template: `
    <app-sidebar></app-sidebar>
    <app-navbar
      [pageTitle]="'Order ' + (order?.orderNumber || '')"
      pageSubtitle="Dedicated order fulfillment & logistics dashboard"
    ></app-navbar>

    <div class="page-wrapper">
      <main class="main-content animate-in">

        <!-- TOP BAR: BACK & ACTIONS -->
        <div class="top-nav-bar">
          <button class="back-link-btn" (click)="goBack()">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            <span>Back to Orders</span>
          </button>

          <div class="top-order-meta" *ngIf="order">
            <span [class]="getStatusBadgeClass(order.orderStatus)">
              {{ order.orderStatus | uppercase }}
            </span>
            <span [class]="getPaymentBadgeClass(order.paymentStatus)">
              {{ order.paymentStatus | uppercase }} · 100% PREPAID
            </span>
            <span class="order-date-tag">
              Placed on {{ order.createdAt | date:'dd MMM yyyy, hh:mm a' }}
            </span>
          </div>
        </div>

        <!-- LOADING STATE -->
        <div *ngIf="loading" class="state-card loading-card">
          <div class="loading-spinner"></div>
          <p>Loading order details...</p>
        </div>

        <!-- ERROR STATE -->
        <div *ngIf="!loading && errorMsg" class="state-card error-card">
          <p>⚠️ {{ errorMsg }}</p>
          <button class="btn btn-secondary btn-sm" (click)="loadOrder()">Retry</button>
        </div>

        <!-- MAIN TWO-COLUMN DASHBOARD -->
        <div class="order-dashboard-grid" *ngIf="!loading && order">

          <!-- ════════ LEFT COLUMN: ITEMS & SHIPROCKET LOGISTICS ════════ -->
          <div class="col-main">

            <!-- 1. ORDERED ITEMS CARD -->
            <section class="luxury-card items-card">
              <div class="card-head">
                <div class="card-title-group">
                  <div class="head-icon icon-gold">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>
                    </svg>
                  </div>
                  <div>
                    <h3 class="card-title">Order Items</h3>
                    <span class="card-subtitle">{{ order.items.length }} product design{{ order.items.length !== 1 ? 's' : '' }}</span>
                  </div>
                </div>
              </div>

              <!-- Item List -->
              <div class="items-list">
                <div *ngFor="let item of order.items" class="order-item-row">
                  <div class="item-img-box">
                    <img *ngIf="item.imageUrl" [src]="item.imageUrl" [alt]="item.title" class="item-img" />
                    <div *ngIf="!item.imageUrl" class="item-img-placeholder">SM</div>
                  </div>
                  <div class="item-details">
                    <h4 class="item-title">{{ item.title }}</h4>
                    <div class="item-tags">
                      <span class="tag-pill" *ngIf="item.deviceModel">📱 {{ item.deviceModel }}</span>
                      <span class="tag-pill" *ngIf="item.material">✨ {{ item.material }}</span>
                      <span class="tag-pill" *ngIf="item.frame">🖼️ {{ item.frame }}</span>
                    </div>
                  </div>
                  <div class="item-pricing">
                    <div class="item-qty">Qty: <strong>{{ item.quantity }}</strong></div>
                    <div class="item-total">₹{{ (item.price * item.quantity) | number }}</div>
                    <div class="item-unit-price" *ngIf="item.quantity > 1">₹{{ item.price | number }} each</div>
                  </div>
                </div>
              </div>

              <!-- Price Breakdown Footer -->
              <div class="order-cost-breakdown">
                <div class="cost-row">
                  <span>Subtotal</span>
                  <span>₹{{ order.subtotal | number }}</span>
                </div>
                <div class="cost-row" *ngIf="order.discount > 0">
                  <span>Discount</span>
                  <span class="text-green">- ₹{{ order.discount | number }}</span>
                </div>
                <div class="cost-row">
                  <span>Shipping</span>
                  <span>{{ order.shippingCharge > 0 ? ('₹' + (order.shippingCharge | number)) : 'FREE' }}</span>
                </div>
                <div class="cost-row total-cost-row">
                  <span>Grand Total Paid</span>
                  <span class="grand-total-amount">₹{{ order.total | number }}</span>
                </div>

                <!-- WhatsApp Order Summary Action Link -->
                <div class="wa-summary-action-box">
                  <a [href]="getShellMuzeWhatsAppUrl()" target="_blank" class="btn-wa-action">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12.012 2c-5.506 0-9.989 4.478-9.99 9.984a9.964 9.964 0 001.333 4.993L2 22l5.233-1.237a9.982 9.982 0 004.779 1.221h.004c5.505 0 9.988-4.478 9.989-9.984 0-2.669-1.038-5.176-2.925-7.062A9.925 9.925 0 0012.012 2zm5.711 14.18c-.244.688-1.42 1.314-1.96 1.398-.507.079-1.163.116-3.8-0.957-3.371-1.373-5.525-4.832-5.694-5.056-.169-.224-1.368-1.82-1.368-3.473 0-1.652.862-2.464 1.167-2.795.304-.332.664-.415.885-.415.221 0 .443.002.636.012.204.01.478-.078.747.568.277.665.946 2.308 1.028 2.474.083.167.139.362.028.583-.11.222-.167.36-.333.554-.167.195-.351.436-.502.585-.166.166-.339.347-.146.679.194.331.862 1.423 1.85 2.303 1.272 1.134 2.345 1.485 2.677 1.651.332.166.526.139.72-.083.194-.222.831-.97 1.053-1.302.221-.332.443-.277.747-.166.304.111 1.936.914 2.268 1.08.332.166.554.249.636.388.083.139.083.803-.161 1.491z"/>
                    </svg>
                    <span>Send Order Summary to Shell Muze (+91 74189 12898)</span>
                    <span class="btn-arrow">↗</span>
                  </a>
                </div>
              </div>
            </section>

            <!-- 2. SHIPROCKET EXPRESS LOGISTICS CARD -->
            <section class="luxury-card shiprocket-logistics-card">
              <div class="card-head">
                <div class="card-title-group">
                  <div class="head-icon icon-purple">
                    <span style="font-size:18px">🚀</span>
                  </div>
                  <div>
                    <h3 class="card-title">Shiprocket Express Logistics</h3>
                    <span class="card-subtitle">Automated fulfillment & live carrier tracking</span>
                  </div>
                </div>
                <span class="badge badge-prepaid-only">
                  ⚡ STRICTLY PREPAID ONLY
                </span>
              </div>

              <!-- STATE A: ORDER NOT YET CREATED IN SHIPROCKET -->
              <div *ngIf="!order.shiprocket?.shipmentId" class="shiprocket-setup-box">
                <div class="setup-intro">
                  <p class="intro-title">Ready for Courier Dispatch</p>
                  <p class="intro-desc">
                    Register this order directly with Shiprocket as a <strong>100% Prepaid</strong> shipment. Once submitted, Shiprocket will create the shipment, assign couriers, and generate printable shipping labels.
                  </p>
                </div>

                <div class="shiprocket-form-grid">
                  <div class="form-group full-width">
                    <label class="input-label">Pickup Warehouse Location</label>
                    <select class="custom-select" [(ngModel)]="shiprocketPickupLocation">
                      <option *ngFor="let loc of pickupLocations" [value]="loc.pickup_location">
                        {{ loc.pickup_location }} — {{ loc.address }}, {{ loc.city }} ({{ loc.pin_code }})
                      </option>
                      <option *ngIf="pickupLocations.length === 0" value="work">
                        work — SHELL MUZE Chennai (600003)
                      </option>
                    </select>
                  </div>

                  <div class="form-group">
                    <label class="input-label">Weight (kg)</label>
                    <div class="input-with-addon">
                      <input type="number" step="0.05" class="custom-input" [(ngModel)]="shiprocketWeight" />
                      <span class="input-addon">kg</span>
                    </div>
                    <div class="preset-pills">
                      <button type="button" class="preset-btn" (click)="shiprocketWeight = 0.25">0.25 kg</button>
                      <button type="button" class="preset-btn" (click)="shiprocketWeight = 0.50">0.50 kg</button>
                      <button type="button" class="preset-btn" (click)="shiprocketWeight = 1.00">1.00 kg</button>
                    </div>
                  </div>

                  <div class="form-group">
                    <label class="input-label">Package Dimensions (L × B × H cm)</label>
                    <div class="dimensions-group">
                      <input type="number" class="custom-input dim-input" [(ngModel)]="shiprocketLength" placeholder="L" />
                      <span class="dim-x">×</span>
                      <input type="number" class="custom-input dim-input" [(ngModel)]="shiprocketBreadth" placeholder="B" />
                      <span class="dim-x">×</span>
                      <input type="number" class="custom-input dim-input" [(ngModel)]="shiprocketHeight" placeholder="H" />
                      <span class="input-addon">cm</span>
                    </div>
                  </div>
                </div>

                <button
                  class="action-btn btn-send-shiprocket"
                  [disabled]="sendingToShiprocket"
                  (click)="sendToShiprocket()"
                >
                  <span *ngIf="!sendingToShiprocket">🚀 Dispatch to Shiprocket (Prepaid)</span>
                  <span *ngIf="sendingToShiprocket" class="btn-spinner-label">
                    <span class="mini-spinner"></span> Connecting to Shiprocket API...
                  </span>
                </button>

                <div *ngIf="shiprocketError" class="alert-box alert-error">
                  <span>⚠️ {{ shiprocketError }}</span>
                </div>
              </div>

              <!-- STATE B: ORDER CREATED IN SHIPROCKET -->
              <div *ngIf="order.shiprocket?.shipmentId" class="shiprocket-active-box">

                <!-- Meta Cards Grid -->
                <div class="shipment-meta-grid">
                  <div class="meta-card">
                    <span class="meta-label">Shipment ID</span>
                    <span class="meta-value text-purple">#{{ order.shiprocket?.shipmentId }}</span>
                  </div>
                  <div class="meta-card">
                    <span class="meta-label">Shiprocket Order #</span>
                    <span class="meta-value">#{{ order.shiprocket?.orderId }}</span>
                  </div>
                  <div class="meta-card">
                    <span class="meta-label">Courier Partner</span>
                    <span class="meta-value font-bold">{{ order.shiprocket?.courierName || 'Pending Assignment' }}</span>
                  </div>
                  <div class="meta-card">
                    <span class="meta-label">AWB Tracking Number</span>
                    <span class="meta-value text-gold font-bold">
                      {{ order.shiprocket?.awbCode || 'Awaiting Generation' }}
                    </span>
                  </div>
                </div>

                <!-- Primary Action Buttons Bar -->
                <div class="shiprocket-actions-bar">
                  <!-- Assign Courier & AWB -->
                  <button
                    *ngIf="!order.shiprocket?.awbCode"
                    class="action-btn btn-assign-awb"
                    [disabled]="generatingAwb"
                    (click)="generateAwb()"
                  >
                    <span *ngIf="!generatingAwb">📦 Assign Courier & Generate AWB</span>
                    <span *ngIf="generatingAwb" class="btn-spinner-label">
                      <span class="mini-spinner"></span> Assigning Courier...
                    </span>
                  </button>

                  <!-- Download Shipping Label -->
                  <button
                    class="action-btn btn-label"
                    [disabled]="generatingLabel"
                    (click)="downloadLabel()"
                  >
                    <span *ngIf="!generatingLabel">🏷️ Download Label (PDF)</span>
                    <span *ngIf="generatingLabel" class="btn-spinner-label">
                      <span class="mini-spinner"></span> Generating Label...
                    </span>
                  </button>

                  <!-- Download Tax Invoice -->
                  <button
                    class="action-btn btn-invoice"
                    [disabled]="generatingInvoice"
                    (click)="downloadInvoice()"
                  >
                    <span *ngIf="!generatingInvoice">🧾 Download Invoice (PDF)</span>
                    <span *ngIf="generatingInvoice" class="btn-spinner-label">
                      <span class="mini-spinner"></span> Generating Invoice...
                    </span>
                  </button>

                  <!-- Real-time Status Sync -->
                  <button
                    class="action-btn btn-sync-status"
                    [disabled]="trackingShipment"
                    (click)="trackShiprocket()"
                  >
                    <span *ngIf="!trackingShipment">🔄 Sync Live Carrier Status</span>
                    <span *ngIf="trackingShipment" class="btn-spinner-label">
                      <span class="mini-spinner"></span> Syncing...
                    </span>
                  </button>

                  <!-- Public Tracking Link -->
                  <a
                    *ngIf="order.shiprocket?.awbCode"
                    [href]="'https://shiprocket.co/tracking/' + order.shiprocket?.awbCode"
                    target="_blank"
                    class="action-btn btn-public-track"
                  >
                    <span>🔗 Track on Shiprocket</span>
                    <span>↗</span>
                  </a>
                </div>

                <!-- Feedback Alerts -->
                <div *ngIf="shiprocketSuccess" class="alert-box alert-success">
                  <span>✅ {{ shiprocketSuccess }}</span>
                </div>
                <div *ngIf="shiprocketError" class="alert-box alert-error">
                  <span>⚠️ {{ shiprocketError }}</span>
                </div>

                <!-- Live Tracking Timeline Feed -->
                <div *ngIf="liveTrackingInfo" class="tracking-feed-card">
                  <div class="feed-header">
                    <div class="status-indicator">
                      <span class="status-pulse-dot"></span>
                      <span class="status-heading">Current Status: <strong>{{ liveTrackingInfo?.current_status || order.shiprocket?.status || 'Shipment Registered' }}</strong></span>
                    </div>
                    <div class="etd-badge" *ngIf="liveTrackingInfo?.etd">
                      Estimated Delivery: <strong>{{ liveTrackingInfo.etd }}</strong>
                    </div>
                  </div>

                  <div *ngIf="liveTrackingInfo?.scans && liveTrackingInfo.scans.length > 0" class="scans-timeline">
                    <div *ngFor="let scan of liveTrackingInfo.scans" class="timeline-step">
                      <div class="timeline-dot"></div>
                      <div class="timeline-content">
                        <div class="step-activity">{{ scan.activity }}</div>
                        <div class="step-meta">{{ scan.date }} · <span class="step-loc">{{ scan.location }}</span></div>
                      </div>
                    </div>
                  </div>

                  <div *ngIf="!liveTrackingInfo?.scans || liveTrackingInfo.scans.length === 0" class="no-scans-message">
                    Package order created in Shiprocket. Awaiting carrier pickup at {{ order.shiprocket?.pickupLocation || 'warehouse' }}.
                  </div>
                </div>

              </div>
            </section>

          </div>

          <!-- ════════ RIGHT COLUMN: CUSTOMER & STATUS UPDATE ════════ -->
          <div class="col-side">

            <!-- 3. CUSTOMER PROFILE CARD -->
            <section class="luxury-card customer-card">
              <div class="card-head">
                <div class="card-title-group">
                  <div class="head-icon icon-blue">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                    </svg>
                  </div>
                  <div>
                    <h3 class="card-title">Customer Details</h3>
                    <span class="card-subtitle">Contact & Shipping</span>
                  </div>
                </div>
              </div>

              <!-- Customer Info Rows -->
              <div class="customer-info-list">
                <div class="info-row">
                  <span class="info-label">Full Name</span>
                  <span class="info-val font-bold">{{ order.customer.name }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Phone</span>
                  <a [href]="'tel:' + order.customer.phone" class="info-link">
                    📞 {{ order.customer.phone }}
                  </a>
                </div>
                <div class="info-row">
                  <span class="info-label">Customer WhatsApp</span>
                  <a [href]="getCustomerWhatsAppUrl()" target="_blank" class="info-link" style="color: #22c55e; font-weight: 700;">
                    💬 Chat with {{ order.customer.name }} ↗
                  </a>
                </div>
                <div class="info-row">
                  <span class="info-label">Email</span>
                  <a [href]="'mailto:' + order.customer.email" class="info-link">
                    ✉️ {{ order.customer.email }}
                  </a>
                </div>

                <div class="info-divider"></div>

                <div class="address-box">
                  <span class="address-title">📍 Delivery Address</span>
                  <p class="address-text">
                    {{ order.shippingAddress.address }}<br>
                    {{ order.shippingAddress.city }}, {{ order.shippingAddress.state }}
                  </p>
                  <span class="pincode-badge">PIN: {{ order.shippingAddress.pincode }}</span>
                </div>

                <div class="info-divider"></div>

                <div class="info-row">
                  <span class="info-label">Payment Gateway</span>
                  <span class="info-val text-green font-bold">Razorpay Online</span>
                </div>
                <div class="info-row" *ngIf="order.razorpayPaymentId">
                  <span class="info-label">Payment ID</span>
                  <span class="info-code">{{ order.razorpayPaymentId }}</span>
                </div>
                <div class="info-row" *ngIf="order.razorpayOrderId">
                  <span class="info-label">Razorpay Order</span>
                  <span class="info-code">{{ order.razorpayOrderId }}</span>
                </div>
              </div>
            </section>

            <!-- 4. ORDER STATUS & LIFECYCLE CARD -->
            <section class="luxury-card lifecycle-card">
              <div class="card-head">
                <div class="card-title-group">
                  <div class="head-icon icon-gold">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                    </svg>
                  </div>
                  <div>
                    <h3 class="card-title">Order Lifecycle</h3>
                    <span class="card-subtitle">Manage internal workflow</span>
                  </div>
                </div>
              </div>

              <div class="lifecycle-form">
                <div class="form-group">
                  <label class="input-label">Order Fulfillment Status</label>
                  <select class="custom-select" [(ngModel)]="orderStatus">
                    <option value="placed">Placed</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="processing">Processing (In Production)</option>
                    <option value="shipped">Shipped (Dispatched)</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div class="form-group">
                  <label class="input-label">Tracking Number</label>
                  <input
                    type="text"
                    class="custom-input"
                    [(ngModel)]="trackingNumber"
                    placeholder="e.g. AWB12345678"
                  />
                </div>

                <div class="form-group">
                  <label class="input-label">Tracking URL</label>
                  <input
                    type="text"
                    class="custom-input"
                    [(ngModel)]="trackingUrl"
                    placeholder="https://shiprocket.co/tracking/..."
                  />
                </div>

                <div class="form-group">
                  <label class="input-label">Internal Admin Notes</label>
                  <textarea
                    class="custom-textarea"
                    rows="3"
                    [(ngModel)]="adminNotes"
                    placeholder="Notes on custom photo uploads, packaging requests, or customer preferences..."
                  ></textarea>
                </div>

                <button
                  class="action-btn btn-save-status"
                  [disabled]="savingStatus"
                  (click)="saveOrderStatus()"
                >
                  <span *ngIf="!savingStatus">Save Order Changes</span>
                  <span *ngIf="savingStatus" class="btn-spinner-label">
                    <span class="mini-spinner"></span> Saving...
                  </span>
                </button>

                <div *ngIf="saveSuccess" class="alert-box alert-success">
                  <span>✅ Order changes saved successfully!</span>
                </div>
              </div>
            </section>

          </div>

        </div>

      </main>
    </div>
  `,
  styles: [`
    .top-nav-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 16px;
      margin-bottom: 24px;
    }
    .back-link-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      color: var(--text-primary);
      padding: 10px 18px;
      border-radius: 10px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
    }
    .back-link-btn:hover {
      background: rgba(255, 255, 255, 0.1);
      transform: translateX(-3px);
      border-color: rgba(201, 169, 110, 0.4);
    }
    .top-order-meta {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
    }
    .order-date-tag {
      font-size: 12px;
      color: var(--text-muted);
    }

    .state-card {
      background: var(--bg-card);
      border: 1px solid var(--border-color);
      border-radius: 16px;
      padding: 60px 24px;
      text-align: center;
    }
    .loading-spinner {
      width: 40px;
      height: 40px;
      border: 3px solid rgba(255, 255, 255, 0.1);
      border-top-color: var(--accent-gold);
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
      margin: 0 auto 16px;
    }
    @keyframes spin { to { transform: rotate(360deg); } }

    /* Grid Layout */
    .order-dashboard-grid {
      display: grid;
      grid-template-columns: 1fr 380px;
      gap: 24px;
      align-items: start;
    }
    @media (max-width: 1080px) {
      .order-dashboard-grid {
        grid-template-columns: 1fr;
      }
    }
    .col-main, .col-side {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    /* Luxury Card */
    .luxury-card {
      background: var(--bg-card);
      border: 1px solid var(--border-color);
      border-radius: 16px;
      padding: 24px;
      box-shadow: var(--shadow-sm);
      transition: border-color 0.2s ease;
    }
    .card-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 20px;
      padding-bottom: 16px;
      border-bottom: 1px solid var(--border-color);
    }
    .card-title-group {
      display: flex;
      align-items: center;
      gap: 12px;
    }
    .head-icon {
      width: 38px;
      height: 38px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .icon-gold { background: rgba(201, 169, 110, 0.12); color: var(--accent-gold); }
    .icon-purple { background: rgba(124, 58, 237, 0.15); color: #a78bfa; }
    .icon-blue { background: rgba(96, 165, 250, 0.15); color: var(--blue); }
    .card-title {
      font-size: 16px;
      font-weight: 700;
      color: var(--text-primary);
      margin: 0;
    }
    .card-subtitle {
      font-size: 11px;
      color: var(--text-muted);
    }

    /* Items List */
    .items-list {
      display: flex;
      flex-direction: column;
      gap: 16px;
      margin-bottom: 20px;
    }
    .order-item-row {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 14px;
      background: rgba(255, 255, 255, 0.02);
      border: 1px solid rgba(255, 255, 255, 0.04);
      border-radius: 12px;
    }
    .item-img-box {
      width: 60px;
      height: 60px;
      border-radius: 10px;
      overflow: hidden;
      flex-shrink: 0;
      background: #0f1624;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid rgba(255, 255, 255, 0.08);
    }
    .item-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .item-img-placeholder {
      font-weight: 700;
      color: var(--text-muted);
      font-size: 14px;
    }
    .item-details {
      flex: 1;
      min-width: 0;
    }
    .item-title {
      font-size: 14px;
      font-weight: 700;
      color: var(--text-primary);
      margin: 0 0 6px 0;
    }
    .item-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }
    .tag-pill {
      font-size: 11px;
      background: rgba(255, 255, 255, 0.06);
      padding: 3px 8px;
      border-radius: 6px;
      color: var(--text-secondary);
    }
    .item-pricing {
      text-align: right;
      flex-shrink: 0;
    }
    .item-qty {
      font-size: 12px;
      color: var(--text-muted);
      margin-bottom: 2px;
    }
    .item-total {
      font-size: 16px;
      font-weight: 800;
      color: var(--accent-gold);
    }
    .item-unit-price {
      font-size: 11px;
      color: var(--text-muted);
    }

    /* Cost Breakdown */
    .order-cost-breakdown {
      border-top: 1px dashed var(--border-color);
      padding-top: 16px;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .cost-row {
      display: flex;
      justify-content: space-between;
      font-size: 13px;
      color: var(--text-secondary);
    }
    .total-cost-row {
      margin-top: 8px;
      padding-top: 12px;
      border-top: 1px solid var(--border-color);
      font-size: 15px;
      font-weight: 700;
      color: var(--text-primary);
    }
    .grand-total-amount {
      font-size: 20px;
      font-weight: 800;
      color: var(--accent-gold);
    }
    .wa-summary-action-box {
      margin-top: 14px;
      padding-top: 14px;
      border-top: 1px dashed var(--border-color);
    }
    .btn-wa-action {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      width: 100%;
      background: rgba(37, 211, 102, 0.12);
      border: 1px solid rgba(37, 211, 102, 0.35);
      color: #22c55e;
      padding: 10px 14px;
      border-radius: var(--radius-md);
      font-size: 12.5px;
      font-weight: 700;
      text-decoration: none;
      transition: all 0.2s ease;
    }
    .btn-wa-action:hover {
      background: #25D366;
      color: #FFFFFF;
      box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);
    }
    .btn-arrow {
      font-size: 14px;
      transition: transform 0.2s ease;
    }
    .btn-wa-action:hover .btn-arrow {
      transform: translate(2px, -2px);
    }

    /* Shiprocket Logistics Card */
    .shiprocket-logistics-card {
      border-color: rgba(124, 58, 237, 0.28);
      background: linear-gradient(180deg, rgba(16, 22, 36, 0.85) 0%, rgba(20, 16, 36, 0.6) 100%);
    }
    .badge-prepaid-only {
      background: rgba(34, 197, 94, 0.15);
      color: #22c55e;
      border: 1px solid rgba(34, 197, 94, 0.3);
      font-size: 11px;
      font-weight: 700;
      padding: 4px 10px;
      border-radius: 999px;
    }

    /* Setup Box */
    .shiprocket-setup-box {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    .setup-intro {
      background: rgba(255, 255, 255, 0.03);
      border: 1px dashed rgba(255, 255, 255, 0.1);
      border-radius: 12px;
      padding: 16px;
    }
    .intro-title {
      font-size: 14px;
      font-weight: 700;
      color: var(--text-primary);
      margin: 0 0 6px 0;
    }
    .intro-desc {
      font-size: 12px;
      color: var(--text-muted);
      margin: 0;
      line-height: 1.6;
    }

    .shiprocket-form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
    }
    .full-width { grid-column: 1 / -1; }
    .input-label {
      display: block;
      font-size: 12px;
      font-weight: 600;
      color: var(--text-secondary);
      margin-bottom: 6px;
    }
    .custom-input, .custom-select, .custom-textarea {
      width: 100%;
      background: var(--bg-input);
      border: 1px solid var(--border-color);
      border-radius: 10px;
      padding: 10px 14px;
      color: var(--text-primary);
      font-size: 13px;
      outline: none;
      transition: all 0.2s ease;
    }
    .custom-input:focus, .custom-select:focus, .custom-textarea:focus {
      border-color: #a78bfa;
      box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.15);
    }
    .input-with-addon {
      position: relative;
      display: flex;
      align-items: center;
    }
    .input-addon {
      position: absolute;
      right: 12px;
      font-size: 11px;
      color: var(--text-muted);
      pointer-events: none;
    }
    .preset-pills {
      display: flex;
      gap: 6px;
      margin-top: 6px;
    }
    .preset-btn {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.08);
      color: var(--text-secondary);
      font-size: 10px;
      padding: 3px 8px;
      border-radius: 6px;
      cursor: pointer;
    }
    .preset-btn:hover {
      background: rgba(255, 255, 255, 0.1);
      color: var(--text-primary);
    }
    .dimensions-group {
      display: flex;
      align-items: center;
      gap: 6px;
      position: relative;
    }
    .dim-input { text-align: center; }
    .dim-x { color: var(--text-muted); font-size: 12px; }

    /* Action Buttons */
    .action-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 12px 20px;
      border-radius: 10px;
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;
      border: none;
      transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
      text-decoration: none;
    }
    .action-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    .btn-send-shiprocket {
      background: linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%);
      color: #ffffff;
      box-shadow: 0 4px 20px rgba(124, 58, 237, 0.35);
    }
    .btn-send-shiprocket:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 24px rgba(124, 58, 237, 0.5);
    }
    .btn-assign-awb {
      background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
      color: #ffffff;
    }
    .btn-label {
      background: rgba(255, 255, 255, 0.08);
      color: #ffffff;
      border: 1px solid rgba(255, 255, 255, 0.15);
    }
    .btn-label:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.15);
      border-color: rgba(255, 255, 255, 0.3);
    }
    .btn-invoice {
      background: rgba(255, 255, 255, 0.08);
      color: #ffffff;
      border: 1px solid rgba(255, 255, 255, 0.15);
    }
    .btn-invoice:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.15);
    }
    .btn-sync-status {
      background: rgba(124, 58, 237, 0.15);
      color: #a78bfa;
      border: 1px solid rgba(124, 58, 237, 0.3);
    }
    .btn-sync-status:hover:not(:disabled) {
      background: rgba(124, 58, 237, 0.25);
    }
    .btn-public-track {
      background: rgba(201, 169, 110, 0.12);
      color: var(--accent-gold);
      border: 1px solid rgba(201, 169, 110, 0.3);
    }
    .btn-public-track:hover {
      background: rgba(201, 169, 110, 0.22);
    }
    .btn-save-status {
      width: 100%;
      background: linear-gradient(135deg, var(--accent-gold) 0%, #b89355 100%);
      color: #080c14;
      font-weight: 800;
      margin-top: 8px;
    }
    .btn-save-status:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 4px 18px rgba(201, 169, 110, 0.35);
    }

    .btn-spinner-label {
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }
    .mini-spinner {
      width: 14px;
      height: 14px;
      border: 2px solid rgba(255, 255, 255, 0.2);
      border-top-color: #ffffff;
      border-radius: 50%;
      animation: spin 0.7s linear infinite;
    }

    /* Active Shipment Box */
    .shiprocket-active-box {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    .shipment-meta-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
      background: rgba(0, 0, 0, 0.3);
      padding: 16px;
      border-radius: 12px;
      border: 1px solid rgba(255, 255, 255, 0.05);
    }
    .meta-card {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .meta-label {
      font-size: 11px;
      color: var(--text-muted);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    .meta-value {
      font-size: 14px;
      color: var(--text-primary);
    }
    .shiprocket-actions-bar {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
    }

    /* Tracking Timeline Feed */
    .tracking-feed-card {
      background: rgba(0, 0, 0, 0.35);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 12px;
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    .feed-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-bottom: 10px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    }
    .status-indicator {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .status-pulse-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #22c55e;
      box-shadow: 0 0 8px #22c55e;
    }
    .status-heading {
      font-size: 13px;
      color: var(--text-primary);
    }
    .etd-badge {
      font-size: 12px;
      color: var(--text-muted);
    }
    .scans-timeline {
      display: flex;
      flex-direction: column;
      gap: 12px;
      max-height: 180px;
      overflow-y: auto;
      padding-left: 8px;
    }
    .timeline-step {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      position: relative;
    }
    .timeline-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--accent-gold);
      margin-top: 4px;
      flex-shrink: 0;
    }
    .step-activity {
      font-size: 12px;
      font-weight: 600;
      color: var(--text-primary);
    }
    .step-meta {
      font-size: 11px;
      color: var(--text-muted);
      margin-top: 2px;
    }
    .step-loc { color: var(--accent-gold-light); }
    .no-scans-message {
      font-size: 12px;
      color: var(--text-muted);
      font-style: italic;
    }

    /* Customer Info List */
    .customer-info-list {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .info-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 13px;
    }
    .info-label {
      color: var(--text-muted);
    }
    .info-val {
      color: var(--text-primary);
    }
    .info-link {
      color: var(--blue);
      text-decoration: none;
      font-weight: 600;
    }
    .info-link:hover { text-decoration: underline; }
    .info-code {
      font-family: monospace;
      font-size: 11px;
      color: var(--text-secondary);
      background: rgba(255, 255, 255, 0.05);
      padding: 2px 6px;
      border-radius: 4px;
    }
    .info-divider {
      height: 1px;
      background: var(--border-color);
      margin: 6px 0;
    }
    .address-box {
      background: rgba(255, 255, 255, 0.02);
      border: 1px solid rgba(255, 255, 255, 0.05);
      border-radius: 10px;
      padding: 12px;
    }
    .address-title {
      display: block;
      font-size: 12px;
      font-weight: 700;
      color: var(--text-primary);
      margin-bottom: 6px;
    }
    .address-text {
      font-size: 12px;
      color: var(--text-secondary);
      line-height: 1.6;
      margin: 0 0 8px 0;
    }
    .pincode-badge {
      display: inline-block;
      font-size: 11px;
      font-weight: 700;
      background: rgba(201, 169, 110, 0.12);
      color: var(--accent-gold);
      padding: 2px 8px;
      border-radius: 4px;
    }

    /* Lifecycle Form */
    .lifecycle-form {
      display: flex;
      flex-direction: column;
      gap: 14px;
    }

    /* Alerts */
    .alert-box {
      padding: 10px 14px;
      border-radius: 8px;
      font-size: 12px;
      line-height: 1.5;
    }
    .alert-success {
      background: rgba(34, 197, 94, 0.12);
      border: 1px solid rgba(34, 197, 94, 0.25);
      color: #22c55e;
    }
    .alert-error {
      background: rgba(239, 68, 68, 0.12);
      border: 1px solid rgba(239, 68, 68, 0.25);
      color: #ef4444;
    }

    /* Utility */
    .text-green { color: #22c55e; }
    .text-purple { color: #a78bfa; }
    .text-gold { color: var(--accent-gold); }
    .font-bold { font-weight: 700; }
  `]
})
export class OrderDetailComponent implements OnInit {
  orderId: string | null = null;
  order: Order | null = null;
  loading = true;
  errorMsg = '';

  // Order Lifecycle form
  orderStatus: OrderStatus = 'placed';
  trackingNumber = '';
  trackingUrl = '';
  adminNotes = '';
  savingStatus = false;
  saveSuccess = false;

  // Shiprocket Logistics
  pickupLocations: any[] = [];
  shiprocketPickupLocation = 'work';
  shiprocketWeight = 0.25;
  shiprocketLength = 15;
  shiprocketBreadth = 10;
  shiprocketHeight = 4;
  sendingToShiprocket = false;
  generatingAwb = false;
  generatingLabel = false;
  generatingInvoice = false;
  trackingShipment = false;
  shiprocketSuccess = '';
  shiprocketError = '';
  liveTrackingInfo: any = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private orderService: OrderService
  ) {}

  ngOnInit(): void {
    this.orderId = this.route.snapshot.paramMap.get('id');
    if (this.orderId) {
      this.loadOrder();
      this.loadPickupLocations();
    } else {
      this.errorMsg = 'No order ID provided';
      this.loading = false;
    }
  }

  loadOrder(): void {
    if (!this.orderId) return;
    this.loading = true;
    this.errorMsg = '';

    this.orderService.getOrderById(this.orderId).subscribe({
      next: (order) => {
        this.order = order;
        this.loading = false;

        this.orderStatus = order.orderStatus;
        this.trackingNumber = order.trackingNumber || '';
        this.trackingUrl = order.trackingUrl || '';
        this.adminNotes = order.adminNotes || '';

        if (order.shiprocket?.pickupLocation) {
          this.shiprocketPickupLocation = order.shiprocket.pickupLocation;
        }
        if (order.shiprocket?.weight) {
          this.shiprocketWeight = order.shiprocket.weight;
        }
        if (order.shiprocket?.length) {
          this.shiprocketLength = order.shiprocket.length;
        }
        if (order.shiprocket?.breadth) {
          this.shiprocketBreadth = order.shiprocket.breadth;
        }
        if (order.shiprocket?.height) {
          this.shiprocketHeight = order.shiprocket.height;
        }
      },
      error: (err) => {
        this.loading = false;
        this.errorMsg = err?.error?.message || err?.message || 'Failed to load order';
      }
    });
  }

  loadPickupLocations(): void {
    this.orderService.getShiprocketPickupLocations().subscribe({
      next: (res) => {
        this.pickupLocations = res.locations || [];
        if (this.pickupLocations.length > 0 && !this.shiprocketPickupLocation) {
          this.shiprocketPickupLocation = this.pickupLocations[0].pickup_location;
        }
      },
      error: (err) => console.warn('Could not load pickup locations', err)
    });
  }

  goBack(): void {
    this.router.navigate(['/orders']);
  }

  // ─── SHIPROCKET ACTIONS ──────────────────────────────────────────────────────

  sendToShiprocket(): void {
    if (!this.order) return;
    this.sendingToShiprocket = true;
    this.shiprocketSuccess = '';
    this.shiprocketError = '';

    this.orderService.createShiprocketOrder(this.order._id, {
      pickupLocation: this.shiprocketPickupLocation,
      weight: this.shiprocketWeight,
      length: this.shiprocketLength,
      breadth: this.shiprocketBreadth,
      height: this.shiprocketHeight
    }).subscribe({
      next: (res) => {
        this.sendingToShiprocket = false;
        this.order = res.order;
        this.orderStatus = res.order.orderStatus;
        this.shiprocketSuccess = `Dispatched to Shiprocket (Prepaid)! Shipment #${res.order.shiprocket?.shipmentId}`;
      },
      error: (err) => {
        this.sendingToShiprocket = false;
        this.shiprocketError = err?.error?.message || err?.message || 'Failed to dispatch order to Shiprocket';
      }
    });
  }

  generateAwb(): void {
    if (!this.order) return;
    this.generatingAwb = true;
    this.shiprocketSuccess = '';
    this.shiprocketError = '';

    this.orderService.generateShiprocketAWB(this.order._id).subscribe({
      next: (res) => {
        this.generatingAwb = false;
        this.order = res.order;
        this.trackingNumber = res.order.trackingNumber || '';
        this.trackingUrl = res.order.trackingUrl || '';
        this.orderStatus = res.order.orderStatus;
        this.shiprocketSuccess = `AWB Code: ${res.order.shiprocket?.awbCode} (${res.order.shiprocket?.courierName || 'Courier Assigned'})`;
      },
      error: (err) => {
        this.generatingAwb = false;
        this.shiprocketError = err?.error?.message || err?.message || 'Failed to generate AWB';
      }
    });
  }

  downloadLabel(): void {
    if (!this.order) return;
    if (this.order.shiprocket?.labelUrl) {
      window.open(this.order.shiprocket.labelUrl, '_blank');
      return;
    }

    this.generatingLabel = true;
    this.shiprocketSuccess = '';
    this.shiprocketError = '';

    this.orderService.generateShiprocketLabel(this.order._id).subscribe({
      next: (res) => {
        this.generatingLabel = false;
        if (res.labelUrl) {
          if (this.order && this.order.shiprocket) {
            this.order.shiprocket.labelUrl = res.labelUrl;
          }
          window.open(res.labelUrl, '_blank');
        } else {
          this.shiprocketSuccess = 'Label generated. Check Shiprocket dashboard if PDF popup was blocked.';
        }
      },
      error: (err) => {
        this.generatingLabel = false;
        this.shiprocketError = err?.error?.message || err?.message || 'Failed to generate label';
      }
    });
  }

  downloadInvoice(): void {
    if (!this.order) return;
    if (this.order.shiprocket?.invoiceUrl) {
      window.open(this.order.shiprocket.invoiceUrl, '_blank');
      return;
    }

    this.generatingInvoice = true;
    this.shiprocketSuccess = '';
    this.shiprocketError = '';

    this.orderService.generateShiprocketInvoice(this.order._id).subscribe({
      next: (res) => {
        this.generatingInvoice = false;
        if (res.invoiceUrl) {
          if (this.order && this.order.shiprocket) {
            this.order.shiprocket.invoiceUrl = res.invoiceUrl;
          }
          window.open(res.invoiceUrl, '_blank');
        } else {
          this.shiprocketSuccess = 'Invoice generated. Check Shiprocket dashboard.';
        }
      },
      error: (err) => {
        this.generatingInvoice = false;
        this.shiprocketError = err?.error?.message || err?.message || 'Failed to generate invoice';
      }
    });
  }

  trackShiprocket(): void {
    if (!this.order) return;
    this.trackingShipment = true;
    this.shiprocketError = '';

    this.orderService.trackShiprocketShipment(this.order._id).subscribe({
      next: (res) => {
        this.trackingShipment = false;
        this.liveTrackingInfo = res.tracking;
        if (res.order) {
          this.order = res.order;
          this.orderStatus = res.order.orderStatus;
        }
        this.shiprocketSuccess = 'Live status synchronized with Shiprocket!';
      },
      error: (err) => {
        this.trackingShipment = false;
        this.shiprocketError = err?.error?.message || err?.message || 'Failed to sync tracking data';
      }
    });
  }

  // ─── STATUS UPDATE ───────────────────────────────────────────────────────────

  saveOrderStatus(): void {
    if (!this.order) return;
    this.savingStatus = true;
    this.saveSuccess = false;

    this.orderService.updateOrderStatus(this.order._id, {
      orderStatus: this.orderStatus,
      trackingNumber: this.trackingNumber,
      trackingUrl: this.trackingUrl,
      adminNotes: this.adminNotes
    }).subscribe({
      next: (updated) => {
        this.order = updated;
        this.savingStatus = false;
        this.saveSuccess = true;
        setTimeout(() => { this.saveSuccess = false; }, 3500);
      },
      error: (err) => {
        this.savingStatus = false;
        alert(err?.error?.message || 'Failed to save order update');
      }
    });
  }

  getStatusBadgeClass(status: string): string {
    const map: Record<string, string> = {
      placed: 'badge badge-grey',
      confirmed: 'badge badge-blue',
      processing: 'badge badge-gold',
      shipped: 'badge badge-purple',
      delivered: 'badge badge-green',
      cancelled: 'badge badge-red'
    };
    return map[status] || 'badge badge-grey';
  }

  getPaymentBadgeClass(status: string): string {
    const map: Record<string, string> = {
      paid: 'badge badge-green',
      pending: 'badge badge-gold',
      failed: 'badge badge-red',
      refunded: 'badge badge-blue'
    };
    return map[status] || 'badge badge-grey';
  }

  getShellMuzeWhatsAppUrl(): string {
    if (!this.order) return 'https://wa.me/917418912898';
    const num = '917418912898';
    const itemsList = this.order.items
      .map((it, idx) => `${idx + 1}. ${it.title} (${it.deviceModel || 'N/A'}, ${it.material || 'Standard'}) × ${it.quantity} - ₹${it.price * it.quantity}`)
      .join('\n');

    const msg = `Hi Shell Muze! 👋\n\nOrder Summary & Dispatch Specs:\n\n📦 *Order Number:* ${this.order.orderNumber}\n👤 *Customer:* ${this.order.customer.name}\n📱 *Phone:* ${this.order.customer.phone}\n📍 *Shipping:* ${this.order.shippingAddress.address}, ${this.order.shippingAddress.city}, ${this.order.shippingAddress.state} - ${this.order.shippingAddress.pincode}\n💳 *Total Amount:* ₹${this.order.total} (Prepaid via Razorpay)\n📊 *Status:* ${this.order.orderStatus.toUpperCase()}\n🚀 *AWB:* ${this.order.shiprocket?.awbCode || 'Pending'}\n\n🛍️ *Items:*\n${itemsList}`;

    return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
  }

  getCustomerWhatsAppUrl(): string {
    if (!this.order) return '#';
    let cleanPhone = (this.order.customer.phone || '').replace(/[^0-9]/g, '');
    if (cleanPhone.length === 10) cleanPhone = '91' + cleanPhone;

    const msg = `Hi ${this.order.customer.name}! 👋\n\nThank you for shopping with Shell Muze. Your order *${this.order.orderNumber}* (Total: ₹${this.order.total}) is currently *${this.order.orderStatus.toUpperCase()}*.\n\n${this.order.shiprocket?.awbCode ? `🚀 *Tracking (AWB):* ${this.order.shiprocket.awbCode} (${this.order.shiprocket.courierName || 'Courier'})\nTrack here: https://shiprocket.co/tracking/${this.order.shiprocket.awbCode}\n\n` : ''}For any queries or design requests, feel free to reply directly here! ✨`;

    return `https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`;
  }
}
